'use client';

import { Button, EmptyState, Skeleton } from '@lulalife/design-system';
import { RefreshCw01 } from '@lulalife/design-system/icons/line';
import type { ReactNode } from 'react';
import { DSSurface } from './DSSurface';

/**
 * The Overview tab's blocks, 24 px apart. Deliberately not DS territory: the legacy editors and
 * cards sit inside and keep their app styles.
 */
export const OverviewSections = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-space-24">{children}</div>
);

/**
 * Local copy of the Figma library's `Empty Block`, not in the package: a dashed block with a message
 * and one or two secondary actions, used instead of a warning alert when a block has no content.
 */
export const SlideoutEmptyBlock = ({
  message,
  actions,
  'data-testid': testId,
}: {
  message?: ReactNode;
  actions?: ReactNode;
  'data-testid'?: string;
}) => (
  <DSSurface
    className="flex w-full flex-col items-center justify-center gap-space-12 rounded-md border border-dashed border-field-border bg-bg-canvas px-space-16 py-space-24"
    data-testid={testId}
  >
    {message ? (
      <div className="text-center text-text-secondary mobile-body-md-base md:desktop-body-md-base">
        {message}
      </div>
    ) : null}
    {actions ? <div className="flex flex-wrap justify-center gap-space-8">{actions}</div> : null}
  </DSSurface>
);

/** Failed load: the centered DS Empty State with Retry (Figma 7501:6300, 8081:15355). */
export const SlideoutLoadFailed = ({ title, onRetry }: { title: string; onRetry: () => void }) => (
  <DSSurface className="flex h-full min-h-[320px] items-center justify-center" role="alert">
    <EmptyState
      type="failed"
      title={title}
      description={null}
      action={
        <Button type="button" variant="secondary" leftIcon={<RefreshCw01 />} onClick={onRetry}>
          Retry
        </Button>
      }
    />
  </DSSurface>
);

/** First load: block skeletons for the whole Overview (Figma 7602:204801, 8081:14976). */
export const SlideoutOverviewSkeleton = () => (
  <DSSurface
    className="flex w-full flex-col gap-space-16"
    role="status"
    aria-label="Loading"
    data-testid="slideout-overview.skeleton"
  >
    <Skeleton width="40%" height={24} />
    <Skeleton width="100%" height={16} />
    <Skeleton width="100%" height={16} />
    <Skeleton width="70%" height={16} />
    <Skeleton width="100%" height={16} />
    <Skeleton width="100%" height={16} />
    <Skeleton width="50%" height={24} />
    <Skeleton width="100%" height={64} />
    <Skeleton width="100%" height={64} />
    <Skeleton width="50%" height={24} />
    <Skeleton width="100%" height={96} />
  </DSSurface>
);
